// src/components/login/admin/brand-aimode-backup-section.tsx
//
// Brand API Keys → Google AI Mode backup (SerpApi), per brand (#89).
//
// When DataForSEO fails an AI Mode call, the call can be filled by SerpApi.
// A brand either uses the platform's backup (Global Settings → Platform), or
// its own SerpApi key so the searches are billed to the brand's account.
//
// Saves on its own, separately from the LLM keys above it: the key is
// validated with SerpApi before it is stored (free, uses no searches), and a
// brand can only be switched to its own key once that key has validated.
import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

import {
  brandAimodeBackupApi,
  type BrandAiModeBackupSettings,
  type BrandAiModeBackupUpdate,
} from "@/api/aimode-backup";
import { Button } from "../../ui/button";

interface Props {
  brand: string;
  inputStyle: React.CSSProperties;
  segBtn: (active: boolean) => React.CSSProperties;
}

function usd(value: number | null | undefined) {
  return typeof value === "number" ? `$${value.toFixed(4)}` : "not set";
}

export function BrandAimodeBackupSection({ brand, inputStyle, segBtn }: Props) {
  const [settings, setSettings] = useState<BrandAiModeBackupSettings | null>(null);
  const [choice, setChoice] = useState<"global" | "own">("global");
  const [keyDraft, setKeyDraft] = useState("");
  const [costDraft, setCostDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  function adopt(next: BrandAiModeBackupSettings) {
    setSettings(next);
    setChoice(next.mode);
    setCostDraft(next.cost_per_search_usd == null ? "" : String(next.cost_per_search_usd));
  }

  useEffect(() => {
    let alive = true;
    setSettings(null);
    setKeyDraft("");
    setMsg(null);
    brandAimodeBackupApi
      .get(brand)
      .then((next) => alive && adopt(next))
      .catch((e) => alive && setMsg({ ok: false, text: String(e?.message || e) }));
    return () => {
      alive = false;
    };
  }, [brand]);

  async function run(action: () => Promise<BrandAiModeBackupSettings>, done: string) {
    setBusy(true);
    setMsg(null);
    try {
      adopt(await action());
      setKeyDraft("");
      setMsg({ ok: true, text: done });
    } catch (e: any) {
      setMsg({ ok: false, text: String(e?.message || e) });
      // A failed re-check is recorded server-side; show what is stored.
      brandAimodeBackupApi.get(brand).then(adopt).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  function save() {
    if (!settings) return;
    const changes: BrandAiModeBackupUpdate = {};
    const key = keyDraft.trim();
    if (choice === "own") {
      if (!key && !settings.key_set) {
        setMsg({ ok: false, text: "Enter this brand's SerpApi key, or switch back to Global." });
        return;
      }
      if (key) changes.api_key = key;
      const cost = costDraft.trim() === "" ? null : Number.parseFloat(costDraft);
      if (cost !== null && !Number.isFinite(cost)) {
        setMsg({ ok: false, text: "Cost per search must be a number." });
        return;
      }
      if (cost !== (settings.cost_per_search_usd ?? null)) changes.cost_per_search_usd = cost;
    }
    changes.mode = choice;
    run(
      () => brandAimodeBackupApi.update(brand, changes),
      choice === "own"
        ? "Saved. This brand's failed AI Mode calls now use its own SerpApi key."
        : "Saved. This brand uses the platform's AI Mode backup.",
    );
  }

  const status = settings?.in_use === "brand"
    ? { ok: true, text: "Failed AI Mode calls for this brand go to its own SerpApi key." }
    : settings?.in_use === "global"
      ? { ok: true, text: "Failed AI Mode calls for this brand go to the platform's SerpApi backup." }
      : { ok: false, text: "No backup applies: the platform backup is off and this brand has no key of its own." };

  return (
    <div style={{ marginTop: 26, paddingTop: 18, borderTop: "1px solid var(--border)" }}>
      <div className="flex items-center justify-between gap-3" style={{ marginBottom: 6 }}>
        <label style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)" }}>
          Google AI Mode backup (SerpApi)
        </label>
        <div
          style={{
            display: "inline-flex",
            padding: 3,
            borderRadius: 9,
            border: "1px solid var(--border)",
            backgroundColor: "var(--input-background)",
          }}
        >
          <button type="button" onClick={() => { setChoice("global"); setMsg(null); }} style={segBtn(choice === "global")}>
            Global
          </button>
          <button type="button" onClick={() => { setChoice("own"); setMsg(null); }} style={segBtn(choice === "own")}>
            Brand key
          </button>
        </div>
      </div>
      <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--muted-foreground)", lineHeight: 1.5 }}>
        Used only when DataForSEO fails an AI Mode call. Global uses the platform's SerpApi key; Brand key
        bills the searches to this brand's own SerpApi account.
      </p>

      {!settings ? (
        <div className="flex items-center gap-2" style={{ fontSize: 13, color: "var(--muted-foreground)" }}>
          <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Loading…
        </div>
      ) : (
        <>
          <div
            className="flex items-start gap-1.5"
            style={{ fontSize: 12, marginBottom: 10, color: status.ok ? "var(--signal-green)" : "var(--muted-foreground)" }}
          >
            {status.ok ? (
              <CheckCircle2 style={{ width: 13, height: 13, flexShrink: 0, marginTop: 2 }} />
            ) : (
              <AlertCircle style={{ width: 13, height: 13, flexShrink: 0, marginTop: 2 }} />
            )}
            <span>{status.text}</span>
          </div>

          {choice === "own" && (
            <>
              <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                  {settings.key_set
                    ? "Leave blank to keep the current key."
                    : "Enter this brand's SerpApi key. It is checked with SerpApi before it is saved."}
                </span>
                {settings.key_set && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: settings.validated ? "var(--signal-green)" : "var(--infrared-coral)",
                    }}
                  >
                    {settings.validated ? "Validated" : "Not validated"} ••••{settings.key_last4}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="password"
                  name="brand-key-serpapi"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore
                  placeholder={settings.key_set ? "Enter a new key to replace" : "Paste SerpApi key"}
                  value={keyDraft}
                  onChange={(e) => { setKeyDraft(e.target.value); setMsg(null); }}
                  style={inputStyle}
                />
                {settings.key_set && (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => run(() => brandAimodeBackupApi.validate(brand), "Key re-checked.")}
                    style={{ flexShrink: 0 }}
                  >
                    Re-check
                  </Button>
                )}
              </div>
              {settings.validation?.ok && settings.validation.account && (
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--muted-foreground)" }}>
                  {settings.validation.account.plan_name ?? "Plan unknown"}
                  {typeof settings.validation.account.total_searches_left === "number"
                    ? ` · ${settings.validation.account.total_searches_left.toLocaleString()} searches left`
                    : ""}
                </p>
              )}
              <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
                <span style={{ fontSize: 12, color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>
                  Cost per search (USD)
                </span>
                <input
                  type="number"
                  step={0.001}
                  min={settings.limits.min_cost_per_search_usd}
                  max={settings.limits.max_cost_per_search_usd}
                  placeholder="From the plan"
                  value={costDraft}
                  onChange={(e) => setCostDraft(e.target.value)}
                  style={{ ...inputStyle, width: 140 }}
                />
                <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                  In use: {usd(settings.effective_cost_per_search_usd)}
                </span>
              </div>
            </>
          )}

          {msg && (
            <div
              className="flex items-center gap-1.5"
              style={{ marginTop: 8, fontSize: 12, color: msg.ok ? "var(--signal-green)" : "var(--infrared-coral)" }}
            >
              {msg.ok ? (
                <CheckCircle2 style={{ width: 13, height: 13, flexShrink: 0 }} />
              ) : (
                <AlertCircle style={{ width: 13, height: 13, flexShrink: 0 }} />
              )}
              <span>{msg.text}</span>
            </div>
          )}

          <div className="flex justify-end gap-2" style={{ marginTop: 14 }}>
            {settings.key_set && (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  if (window.confirm("Remove this brand's SerpApi key? The brand will use the platform backup.")) {
                    run(() => brandAimodeBackupApi.update(brand, { clear_key: true }), "Key removed. This brand now uses the platform backup.");
                  }
                }}
                style={{ color: "var(--infrared-coral)" }}
              >
                Remove key
              </Button>
            )}
            <Button onClick={save} disabled={busy} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {busy && <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} />}
              Save AI Mode backup
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default BrandAimodeBackupSection;
